import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import CaptionPreview from './CaptionPreview';

const FONTS = [
    'Inter', 'Inter Bold', 'Montserrat', 'Montserrat Bold',
    'Roboto', 'Roboto Bold', 'Roboto Condensed', 'Poppins', 'Poppins Bold',
    'Arial', 'Arial Bold', 'Helvetica', 'Anton', 'Bebas Neue', 'Nunito'
];

const COLOR_PRESETS = [
    '#04FF00', '#FFB800', '#FF6B6B', '#00D4FF', '#FF00FF', '#FFFFFF', '#00FFFF'
];

// Subtitle Style Presets with complete configurations
const SUBTITLE_PRESETS = {
    karaoke: {
        id: 'karaoke',
        name: 'Karaoke',
        description: 'Word-by-word highlight',
        font: 'Inter Bold',
        weight: 700,
        textCase: 'uppercase',
        textColor: '#FFFFFF',
        highlightColor: '#00FF66',
        bgColor: 'transparent',
        animation: 'word-highlight',
        wordByWord: true,
        preview: { base: 'TO', highlight: 'GET', suffix: 'STARTED' }
    },
    beasty: {
        id: 'beasty',
        name: 'Beasty',
        description: 'Scale punch effect',
        font: 'Anton',
        weight: 800,
        textCase: 'uppercase',
        textColor: '#FFFFFF',
        highlightColor: '#FFB800',
        bgColor: 'transparent',
        animation: 'scale-punch',
        wordByWord: true,
        preview: { base: 'CHOOSE A', highlight: 'STYLE', suffix: '' }
    },
    deepdiver: {
        id: 'deepdiver',
        name: 'Deep Diver',
        description: 'Phrase fade + slide',
        font: 'Inter',
        weight: 400,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#888888',
        bgColor: 'rgba(0,0,0,0.5)',
        animation: 'fade-slide',
        wordByWord: false,
        preview: { base: 'to get', highlight: 'started', suffix: '' }
    },
    youshaei: {
        id: 'youshaei',
        name: 'Youshaei',
        description: 'Keyword accent',
        font: 'Inter',
        weight: 600,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#04FF00',
        bgColor: 'transparent',
        animation: 'opacity-reveal',
        wordByWord: false,
        preview: { base: 'TO', highlight: 'GET', suffix: 'STARTED' }
    },
    podp: {
        id: 'podp',
        name: 'Pod P',
        description: 'Podcast-safe',
        font: 'Roboto Condensed',
        weight: 700,
        textCase: 'uppercase',
        textColor: '#000000',
        highlightColor: '#04FF00',
        bgColor: '#04FF00',
        animation: 'none',
        wordByWord: false,
        preview: { base: 'GET', highlight: 'STARTED', suffix: '' }
    },
    mozi: {
        id: 'mozi',
        name: 'Mozi',
        description: 'Word pop-in',
        font: 'Poppins Bold',
        weight: 700,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#FF00FF',
        bgColor: 'transparent',
        animation: 'pop-in',
        wordByWord: true,
        preview: { base: 'CHOOSE A', highlight: 'STYLE', suffix: '' }
    },
    popline: {
        id: 'popline',
        name: 'Popline',
        description: 'Line slide-up',
        font: 'Montserrat Bold',
        weight: 800,
        textCase: 'uppercase',
        textColor: '#FFFFFF',
        highlightColor: '#FFFFFF',
        bgColor: 'transparent',
        animation: 'slide-up',
        wordByWord: false,
        preview: { base: 'TO GET', highlight: 'STARTED', suffix: '' }
    },
    simple: {
        id: 'simple',
        name: 'Simple',
        description: 'Clean & minimal',
        font: 'Inter',
        weight: 400,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#FFFFFF',
        bgColor: 'rgba(0,0,0,0.6)',
        animation: 'fade',
        wordByWord: false,
        preview: { base: 'CHOOSE A', highlight: '', suffix: '' }
    },
    thinkmedia: {
        id: 'thinkmedia',
        name: 'Think Media',
        description: 'Keyword highlight',
        font: 'Inter',
        weight: 600,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#04FF00',
        bgColor: 'transparent',
        animation: 'keyword-swap',
        wordByWord: false,
        preview: { base: 'TO GET', highlight: 'STARTED', suffix: '' }
    }
};

const StyleSettingsModal = ({
    isOpen,
    onClose,
    onSave,
    videoThumbnail,
    videoTitle = '',
    styleSettings,
    setStyleSettings
}) => {
    if (!isOpen) return null;

    const updateHookElement = (element, field, value) => {
        setStyleSettings(prev => ({
            ...prev,
            [element]: { ...prev[element], [field]: value }
        }));
    };

    const updateField = (field, value) => {
        setStyleSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        onSave?.(styleSettings);
        onClose();
    };

    // Apply a subtitle preset
    const applyPreset = (presetId) => {
        const preset = SUBTITLE_PRESETS[presetId];
        if (!preset) return;

        setStyleSettings(prev => ({
            ...prev,
            subtitlePreset: presetId,
            subtitleFont: preset.font,
            subtitleTextColor: preset.textColor,
            subtitleHighlightColor: preset.highlightColor,
            subtitleBgColor: preset.bgColor === 'transparent' ? '#00000000' : preset.bgColor,
            subtitleOpacity: preset.bgColor === 'transparent' || preset.bgColor.includes('rgba') ? 0.6 : 1,
            subtitleWordByWord: preset.wordByWord,
            subtitleAnimation: preset.animation,
            subtitleTextCase: preset.textCase,
            subtitleStroke: presetId !== 'simple' && presetId !== 'deepdiver' && presetId !== 'podp'
        }));
    };

    // Calculate hook X position for preview
    const getHookX = () => {
        switch (styleSettings.hookAlignment) {
            case 'left': return '10%';
            case 'center': return '50%';
            case 'right': return '90%';
            default: return '10%';
        }
    };

    const getHookTransform = () => {
        switch (styleSettings.hookAlignment) {
            case 'left': return 'translateX(0)';
            case 'center': return 'translateX(-50%)';
            case 'right': return 'translateX(-100%)';
            default: return 'translateX(0)';
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[110] p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-dark-400 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-dark-200/50 shadow-2xl"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-dark-200/50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                                <span className="text-lg">✨</span>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">Style Settings</h2>
                                <p className="text-sm text-cream-400">Customize your video overlay & subtitles</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-dark-200 rounded-lg transition">
                            <X className="w-5 h-5 text-cream-400" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col md:flex-row max-h-[calc(90vh-180px)] overflow-hidden">
                        {/* Preview Panel */}
                        <div className="w-full md:w-2/5 p-6 border-b md:border-b-0 md:border-r border-dark-200/30 flex flex-col items-center justify-center bg-dark-400/50">
                            <div
                                className="relative w-full aspect-[9/16] max-w-[280px] rounded-2xl overflow-hidden border border-dark-200/50"
                                style={{ backgroundColor: '#0a1628' }}
                            >
                                {videoThumbnail && (
                                    <img src={videoThumbnail} alt="Preview" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                                )}

                                {/* Live Animated Caption Preview */}
                                {styleSettings.subtitleEnabled && (
                                    <CaptionPreview
                                        blocks={[]}
                                        presetId={styleSettings.subtitlePreset || 'karaoke'}
                                        videoThumbnail={videoThumbnail}
                                        autoPlay={true}
                                        position={styleSettings.subtitlePosition || 80}
                                    />
                                )}

                                {/* Hook Overlay Preview */}
                                {styleSettings.hookEnabled && (
                                    <div
                                        className="absolute"
                                        style={{
                                            left: getHookX(),
                                            top: `${styleSettings.hookVerticalPosition}%`,
                                            transform: getHookTransform(),
                                            textAlign: styleSettings.hookAlignment
                                        }}
                                    >
                                        {styleSettings.hookBrand?.text && (
                                            <div
                                                className="text-xs font-medium mb-1"
                                                style={{
                                                    color: styleSettings.hookBrand.color,
                                                    fontFamily: styleSettings.hookBrand.font,
                                                    textShadow: styleSettings.hookStroke ? `1px 1px 0 ${styleSettings.hookStrokeColor}, -1px -1px 0 ${styleSettings.hookStrokeColor}, 1px -1px 0 ${styleSettings.hookStrokeColor}, -1px 1px 0 ${styleSettings.hookStrokeColor}` : 'none'
                                                }}
                                            >
                                                {styleSettings.hookBrand.text || 'NAMA / BRAND'}
                                            </div>
                                        )}
                                        <div
                                            className="text-2xl font-black"
                                            style={{
                                                color: styleSettings.hookHeadline?.color || '#04FF00',
                                                fontFamily: styleSettings.hookHeadline?.font,
                                                textShadow: styleSettings.hookStroke ? `2px 2px 0 ${styleSettings.hookStrokeColor}, -2px -2px 0 ${styleSettings.hookStrokeColor}, 2px -2px 0 ${styleSettings.hookStrokeColor}, -2px 2px 0 ${styleSettings.hookStrokeColor}` : 'none'
                                            }}
                                        >
                                            {styleSettings.hookHeadline?.text || 'HEADLINE'}
                                        </div>
                                        {styleSettings.hookSubtitle?.text && (
                                            <div
                                                className="text-sm mt-1"
                                                style={{
                                                    color: styleSettings.hookSubtitle.color,
                                                    fontFamily: styleSettings.hookSubtitle.font,
                                                    textShadow: styleSettings.hookStroke ? `1px 1px 0 ${styleSettings.hookStrokeColor}, -1px -1px 0 ${styleSettings.hookStrokeColor}, 1px -1px 0 ${styleSettings.hookStrokeColor}, -1px 1px 0 ${styleSettings.hookStrokeColor}` : 'none'
                                                }}
                                            >
                                                {styleSettings.hookSubtitle.text || 'Subjudul kecil...'}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-cream-500 mt-4">Live Preview</p>
                        </div>

                        {/* Settings Panel */}
                        <div className="w-full md:w-3/5 p-6 overflow-y-auto space-y-6">
                            {/* Hook Overlay Section */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">T</span>
                                        <span className="font-semibold text-white">Hook Overlay</span>
                                        <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-medium">HOT</span>
                                    </div>
                                    <button
                                        onClick={() => updateField('hookEnabled', !styleSettings.hookEnabled)}
                                        className={`w-12 h-6 rounded-full transition-all ${styleSettings.hookEnabled ? 'bg-amber-500' : 'bg-dark-200'}`}
                                    >
                                        <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-all ${styleSettings.hookEnabled ? 'ml-6' : 'ml-0.5'}`} />
                                    </button>
                                </div>

                                {styleSettings.hookEnabled && (
                                    <div className="space-y-4 pl-2 border-l-2 border-dark-200/50">
                                        {/* Brand */}
                                        <div className="space-y-2">
                                            <label className="text-xs text-cream-400">Brand Name (Top)</label>
                                            <input
                                                type="text"
                                                value={styleSettings.hookBrand?.text || ''}
                                                onChange={(e) => updateHookElement('hookBrand', 'text', e.target.value)}
                                                placeholder="NAMA / BRAND"
                                                className="w-full bg-dark-300/50 border border-dark-200 rounded-lg px-4 py-3 text-white placeholder-cream-400 focus:outline-none focus:border-amber-500/50"
                                            />
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="text-xs text-cream-500 mb-1 block">Color</label>
                                                    <div className="flex gap-1">
                                                        {COLOR_PRESETS.map(color => (
                                                            <button
                                                                key={`brand-${color}`}
                                                                onClick={() => updateHookElement('hookBrand', 'color', color)}
                                                                className={`w-6 h-6 rounded-full border-2 transition ${styleSettings.hookBrand?.color === color ? 'border-white scale-110' : 'border-transparent'}`}
                                                                style={{ backgroundColor: color }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex-1">
                                                    <label className="text-xs text-cream-500 mb-1 block">Font</label>
                                                    <select
                                                        value={styleSettings.hookBrand?.font || 'Inter'}
                                                        onChange={(e) => updateHookElement('hookBrand', 'font', e.target.value)}
                                                        className="w-full bg-dark-300/50 border border-dark-200 rounded-lg px-2 py-1 text-white text-sm"
                                                    >
                                                        {FONTS.map(font => <option key={font} value={font}>{font}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Headline */}
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <label className="text-xs text-cream-400">Headline (Main)</label>
                                                {videoTitle && (
                                                    <button
                                                        onClick={() => updateHookElement('hookHeadline', 'text', videoTitle)}
                                                        className="text-[11px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition"
                                                        title={`Use: "${videoTitle}"`}
                                                    >
                                                        Use Video Title
                                                    </button>
                                                )}
                                            </div>
                                            <input
                                                type="text"
                                                value={styleSettings.hookHeadline?.text || ''}
                                                onChange={(e) => updateHookElement('hookHeadline', 'text', e.target.value)}
                                                placeholder={videoTitle ? `e.g. "${videoTitle.substring(0, 40)}${videoTitle.length > 40 ? '...' : ''}"` : 'HEADLINE'}
                                                className="w-full bg-dark-300/50 border border-dark-200 rounded-lg px-4 py-3 text-white placeholder-cream-400 focus:outline-none focus:border-amber-500/50"
                                            />
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="text-xs text-cream-500 mb-1 block">Color</label>
                                                    <div className="flex gap-1">
                                                        {COLOR_PRESETS.map(color => (
                                                            <button
                                                                key={`headline-${color}`}
                                                                onClick={() => updateHookElement('hookHeadline', 'color', color)}
                                                                className={`w-6 h-6 rounded-full border-2 transition ${styleSettings.hookHeadline?.color === color ? 'border-white scale-110' : 'border-transparent'}`}
                                                                style={{ backgroundColor: color }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex-1">
                                                    <label className="text-xs text-cream-500 mb-1 block">Font</label>
                                                    <select
                                                        value={styleSettings.hookHeadline?.font || 'Inter Bold'}
                                                        onChange={(e) => updateHookElement('hookHeadline', 'font', e.target.value)}
                                                        className="w-full bg-dark-300/50 border border-dark-200 rounded-lg px-2 py-1 text-white text-sm"
                                                    >
                                                        {FONTS.map(font => <option key={font} value={font}>{font}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Subtitle */}
                                        <div className="space-y-2">
                                            <label className="text-xs text-cream-400">Subtitle (Bottom)</label>
                                            <input
                                                type="text"
                                                value={styleSettings.hookSubtitle?.text || ''}
                                                onChange={(e) => updateHookElement('hookSubtitle', 'text', e.target.value)}
                                                placeholder="Subjudul kecil..."
                                                className="w-full bg-dark-300/50 border border-amber-500/50 rounded-lg px-4 py-3 text-white placeholder-cream-400 focus:outline-none"
                                            />
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="text-xs text-cream-500 mb-1 block">Color</label>
                                                    <div className="flex gap-1">
                                                        {COLOR_PRESETS.map(color => (
                                                            <button
                                                                key={`sub-${color}`}
                                                                onClick={() => updateHookElement('hookSubtitle', 'color', color)}
                                                                className={`w-6 h-6 rounded-full border-2 transition ${styleSettings.hookSubtitle?.color === color ? 'border-white scale-110' : 'border-transparent'}`}
                                                                style={{ backgroundColor: color }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex-1">
                                                    <label className="text-xs text-cream-500 mb-1 block">Font</label>
                                                    <select
                                                        value={styleSettings.hookSubtitle?.font || 'Inter'}
                                                        onChange={(e) => updateHookElement('hookSubtitle', 'font', e.target.value)}
                                                        className="w-full bg-dark-300/50 border border-dark-200 rounded-lg px-2 py-1 text-white text-sm"
                                                    >
                                                        {FONTS.map(font => <option key={font} value={font}>{font}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Alignment */}
                                        <div className="space-y-2">
                                            <label className="text-xs text-cream-400">Alignment</label>
                                            <div className="flex gap-2">
                                                {['left', 'center', 'right'].map((align) => (
                                                    <button
                                                        key={align}
                                                        onClick={() => updateField('hookAlignment', align)}
                                                        className={`flex-1 py-2 rounded-lg border transition text-sm font-medium ${styleSettings.hookAlignment === align
                                                            ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                                                            : 'bg-dark-300/50 border-dark-200 text-cream-400 hover:border-dark-100'
                                                            }`}
                                                    >
                                                        {align.charAt(0).toUpperCase() + align.slice(1)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Vertical Position */}
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <label className="text-xs text-cream-400">Vertical Position</label>
                                                <span className="text-xs text-cream-500">{styleSettings.hookVerticalPosition}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={styleSettings.hookVerticalPosition}
                                                onChange={(e) => updateField('hookVerticalPosition', parseInt(e.target.value))}
                                                className="w-full accent-amber-500"
                                            />
                                        </div>

                                        {/* Stroke / Outline */}
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={styleSettings.hookStroke}
                                                    onChange={(e) => updateField('hookStroke', e.target.checked)}
                                                    className="w-4 h-4 accent-amber-500"
                                                />
                                                <div>
                                                    <p className="text-sm text-white">Enable Stroke (Outline)</p>
                                                    <p className="text-xs text-cream-500">Add black outline for readability</p>
                                                </div>
                                            </div>
                                            {styleSettings.hookStroke && (
                                                <div className="pl-7 space-y-2">
                                                    <label className="text-xs text-cream-400">Stroke Color</label>
                                                    <div className="flex gap-1">
                                                        {['#000000', '#FFFFFF', '#1a1a2e'].map(color => (
                                                            <button
                                                                key={`stroke-${color}`}
                                                                onClick={() => updateField('hookStrokeColor', color)}
                                                                className={`w-6 h-6 rounded-full border-2 transition ${styleSettings.hookStrokeColor === color ? 'border-amber-500 scale-110' : 'border-dark-100'}`}
                                                                style={{ backgroundColor: color }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Divider */}
                            <div className="border-t border-dark-200/30" />

                            {/* Subtitle Section */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">⩩</span>
                                        <span className="font-semibold text-white">Subtitle Style</span>
                                    </div>
                                    <button
                                        onClick={() => updateField('subtitleEnabled', !styleSettings.subtitleEnabled)}
                                        className={`w-12 h-6 rounded-full transition-all ${styleSettings.subtitleEnabled ? 'bg-amber-500' : 'bg-dark-200'}`}
                                    >
                                        <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-all ${styleSettings.subtitleEnabled ? 'ml-6' : 'ml-0.5'}`} />
                                    </button>
                                </div>

                                {styleSettings.subtitleEnabled && (
                                    <div className="space-y-4 pl-2 border-l-2 border-dark-200/50">
                                        {/* PRESET SELECTOR */}
                                        <div className="space-y-3">
                                            <label className="text-xs text-cream-400">Choose a caption style preset to get started</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                {Object.values(SUBTITLE_PRESETS).map((preset) => (
                                                    <button
                                                        key={preset.id}
                                                        onClick={() => applyPreset(preset.id)}
                                                        className={`p-3 rounded-xl border-2 transition-all text-center ${styleSettings.subtitlePreset === preset.id
                                                            ? 'border-amber-500 bg-amber-500/10'
                                                            : 'border-dark-200 bg-dark-300/50 hover:border-dark-100'
                                                            }`}
                                                    >
                                                        {/* Preview Text */}
                                                        <div
                                                            className="text-xs font-bold mb-2 h-8 flex items-center justify-center"
                                                            style={{
                                                                fontFamily: preset.font.replace(' Bold', ''),
                                                                fontWeight: preset.weight,
                                                                textTransform: preset.textCase === 'uppercase' ? 'uppercase' : 'none',
                                                                backgroundColor: preset.bgColor === 'transparent' ? 'transparent' : preset.bgColor,
                                                                padding: '4px 8px',
                                                                borderRadius: '4px'
                                                            }}
                                                        >
                                                            <span style={{ color: preset.textColor }}>{preset.preview.base} </span>
                                                            <span style={{ color: preset.highlightColor }}>{preset.preview.highlight}</span>
                                                            {preset.preview.suffix && <span style={{ color: preset.textColor }}> {preset.preview.suffix}</span>}
                                                        </div>
                                                        <p className="text-[11px] text-cream-400">{preset.name}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="border-t border-dark-200/30 pt-4"></div>

                                        {/* Font */}
                                        <div className="space-y-2">
                                            <label className="text-xs text-cream-400">Font</label>
                                            <select
                                                value={styleSettings.subtitleFont}
                                                onChange={(e) => updateField('subtitleFont', e.target.value)}
                                                className="w-full bg-dark-300/50 border border-dark-200 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500/50"
                                            >
                                                {FONTS.map(font => <option key={font} value={font}>{font}</option>)}
                                            </select>
                                        </div>

                                        {/* Colors */}
                                        <div className="space-y-2">
                                            <label className="text-xs text-cream-400">Colors</label>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="space-y-2">
                                                    <div
                                                        className="w-full aspect-square rounded-lg border-2 border-dark-100"
                                                        style={{ backgroundColor: styleSettings.subtitleTextColor }}
                                                    />
                                                    <p className="text-xs text-cream-500 text-center">Text</p>
                                                    <div className="flex gap-1 flex-wrap justify-center">
                                                        {COLOR_PRESETS.map(color => (
                                                            <button
                                                                key={`subtext-${color}`}
                                                                onClick={() => updateField('subtitleTextColor', color)}
                                                                className={`w-4 h-4 rounded-full border transition ${styleSettings.subtitleTextColor === color ? 'border-white scale-110' : 'border-transparent'}`}
                                                                style={{ backgroundColor: color }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <div
                                                        className="w-full aspect-square rounded-lg border-2 border-dark-100"
                                                        style={{ backgroundColor: styleSettings.subtitleOutlineColor }}
                                                    />
                                                    <p className="text-xs text-cream-500 text-center">Outline</p>
                                                    <div className="flex gap-1 flex-wrap justify-center">
                                                        {['#000000', '#FFFFFF', '#FFB800', '#FF6B6B', '#04FF00'].map(color => (
                                                            <button
                                                                key={`suboutline-${color}`}
                                                                onClick={() => updateField('subtitleOutlineColor', color)}
                                                                className={`w-4 h-4 rounded-full border transition ${styleSettings.subtitleOutlineColor === color ? 'border-amber-500 scale-110' : 'border-dark-100'}`}
                                                                style={{ backgroundColor: color }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <div
                                                        className="w-full aspect-square rounded-lg border-2 border-dark-100"
                                                        style={{ backgroundColor: styleSettings.subtitleBgColor }}
                                                    />
                                                    <p className="text-xs text-cream-500 text-center">BG</p>
                                                    <div className="flex gap-1 flex-wrap justify-center">
                                                        {['#000000', '#FFFFFF', '#1a1a2e', '#0f172a', 'transparent'].map(color => (
                                                            <button
                                                                key={`subbg-${color}`}
                                                                onClick={() => updateField('subtitleBgColor', color === 'transparent' ? '#00000000' : color)}
                                                                className={`w-4 h-4 rounded-full border transition ${styleSettings.subtitleBgColor === color || (color === 'transparent' && styleSettings.subtitleBgColor === '#00000000') ? 'border-amber-500 scale-110' : 'border-dark-100'}`}
                                                                style={{ backgroundColor: color === 'transparent' ? 'transparent' : color, backgroundImage: color === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' : 'none', backgroundSize: '4px 4px' }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Opacity */}
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <label className="text-xs text-cream-400">Opacity</label>
                                                <span className="text-xs text-cream-500">{Math.round(styleSettings.subtitleOpacity * 100)}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={styleSettings.subtitleOpacity * 100}
                                                onChange={(e) => updateField('subtitleOpacity', parseInt(e.target.value) / 100)}
                                                className="w-full accent-amber-500"
                                            />
                                        </div>

                                        {/* Vertical Position */}
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <label className="text-xs text-cream-400">Vertical Position</label>
                                                <span className="text-xs text-cream-500">{styleSettings.subtitlePosition}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={styleSettings.subtitlePosition}
                                                onChange={(e) => updateField('subtitlePosition', parseInt(e.target.value))}
                                                className="w-full accent-amber-500"
                                            />
                                        </div>

                                        {/* Enable Stroke */}
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={styleSettings.subtitleStroke}
                                                onChange={(e) => updateField('subtitleStroke', e.target.checked)}
                                                className="w-4 h-4 accent-amber-500"
                                            />
                                            <div>
                                                <p className="text-sm text-white">Enable Stroke</p>
                                                <p className="text-xs text-cream-500">Add outline to text</p>
                                            </div>
                                        </div>

                                        {/* Word by Word */}
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={styleSettings.subtitleWordByWord}
                                                onChange={(e) => updateField('subtitleWordByWord', e.target.checked)}
                                                className="w-4 h-4 accent-amber-500"
                                            />
                                            <div>
                                                <p className="text-sm text-white">Word-by-Word Highlight</p>
                                                <p className="text-xs text-cream-500">Karaoke-style word highlighting</p>
                                            </div>
                                        </div>

                                        {styleSettings.subtitleWordByWord && (
                                            <div className="space-y-2">
                                                <label className="text-xs text-cream-400">Highlight Color</label>
                                                <div className="flex gap-1">
                                                    {COLOR_PRESETS.map(color => (
                                                        <button
                                                            key={`highlight-${color}`}
                                                            onClick={() => updateField('subtitleHighlightColor', color)}
                                                            className={`w-6 h-6 rounded-full border-2 transition ${styleSettings.subtitleHighlightColor === color ? 'border-white scale-110' : 'border-transparent'}`}
                                                            style={{ backgroundColor: color }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 p-6 border-t border-dark-200/50 bg-dark-400/50">
                        <button
                            onClick={onClose}
                            className="px-6 py-3 rounded-xl text-cream-400 hover:bg-dark-200 transition font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-black font-bold transition"
                        >
                            Save Settings
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default StyleSettingsModal;
